// ════════════════════════════════════════════════════════════════════
// TERROR TCG — GraceVault.jsx   ·   THE FOIL VAULT'S COMPANION SHELF
// ────────────────────────────────────────────────────────────────────
// caP'n, 2026-09-16: "the player should be able to purchase a King's
// Grace version of their companion from the booster shop foil vault …
// every single foil that exists in the Terror foil vault is King's
// Grace … let's make sure the companions section for all 40 unique
// companions get the treatment."
//
// So: all 40 companions × the ten Vault foils, priced off the SAME
// table the cards use (storyData.FOIL_VAULT_PRICE), in ◆, one per
// account per copy. The write is kingsGrace.buyGrace — this file is a
// shelf and nothing else; it never decides a price's legality or
// whether a sale is allowed, it only asks.
//
// PERFORMANCE / THE CONTEXT BUDGET LAW (PRIME §2.20). Forty tiles each
// holding a live Grace is ~1,500 animated nodes, and forty 3D bodies is
// forty WebGL contexts. So the tiles are webp with the Grace FROZEN on
// a seeded frame; only the tile under your hand animates. The one live
// 3D picture is THE FITTING ROOM at the top — one canvas, whichever
// companion and Grace you have selected.
// ════════════════════════════════════════════════════════════════════
import { useMemo, useState } from "react";
import {
  GM, OBSIDIAN_SLAB, OBSIDIAN_SLAB_LIT, OBSIDIAN_RECESS, GOLD_METAL,
  facetTB, SHADOW_RAISED, SHADOW_RECESS, TEXT_SET,
} from "./theme";
import { COMPANIONS, ILK_COLORS, isCompanionUnlocked, getUnlockHint } from "./companions";
import {
  GRACES, TERROR_GRACE_ORDER, buyGrace, isGraceSold, ownsGrace, graceDriftFrom,
  registerGracePrices, gracePriceIn, registerGraceEligibility,
} from "./kingsGrace";
import { FOIL_VAULT_PRICE } from "./storyData";
import { HOLO_TIER_INFO } from "./PackArt";
import CompanionSprite from "./CompanionSprite";
import { CompanionBody3D, companionBodyOf } from "./companionBody3D";
import SourceSigil from "./SourceSigil";
import { DivineCrest } from "./CoinGlyph";

// The Grace registry mirrors PackArt's tier table; say so if it drifts.
if (process.env.NODE_ENV !== "production") {
  const drift = graceDriftFrom(HOLO_TIER_INFO);
  if (drift.length) console.warn("[King's Grace] registry drifted from HOLO_TIER_INFO:", drift);
}

// Terror's Graces cost what the same foil costs on a card — one table.
// Registered with the kit so the WRITE reads the price, never a caller.
registerGracePrices("terror", (graceId) => FOIL_VAULT_PRICE[graceId]);
// ⚑ ANYONE MAY BUY (caP'n, 2026-09-16): "player buys level 3 Spectral Tiger
// Lord Grace from either game — you can only use and see that grace when you
// have a lvl 3 STL." The gate is on WEARING (equipGrace needs the companion
// at your side), never on buying. Reversible: delete this line and the kit's
// default rule (own the companion) comes back.
registerGraceEligibility("terror", () => null);
export const gracePrice = (graceId) => gracePriceIn("terror", graceId);

const TIER_FILTERS = [
  { key: "all",      label: "All" },
  { key: "mine",     label: "✦ Mine" },
  { key: "beb",      label: "Bebs" },
  { key: "champion", label: "Champions" },
  { key: "lord",     label: "Lords" },
  { key: "trophy",   label: "Trophies" },
];
const TIER_RANK = { beb: 1, champion: 2, lord: 3, mythic: 4 };

function GraceGems({ value, onPick, size = 14 }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center" }}>
      {TERROR_GRACE_ORDER.map((t) => {
        const g = GRACES[t];
        const active = t === value;
        return (
          <button key={t} title={`${g.label} — ${g.title}`} onClick={(e) => { e.stopPropagation(); onPick(t); }}
            style={{
              width: size, height: size, borderRadius: "50%", cursor: "pointer", padding: 0,
              background: g.color,
              border: active ? `2px solid ${GM.vellum}` : `1px solid ${g.color}88`,
              boxShadow: active ? `0 0 8px ${g.color}` : "none",
              transform: active ? "scale(1.18)" : "scale(1)", transition: "all 0.12s",
            }} />
        );
      })}
    </div>
  );
}

// ── one companion, buyable in any of the ten Graces ────────────────
function GraceTile({ companion, profile, selected, onSelect, busy, onBuy }) {
  const [grace, setGrace] = useState(companion.id === "jynxi" ? "holo_jynxi" : "holo_gold");
  const [hovered, setHovered] = useState(false);
  const g = GRACES[grace];
  const unlocked = isCompanionUnlocked(profile, companion.id);
  const sold = isGraceSold(profile, companion.id, grace, "vault");
  const heldElsewhere = !sold && ownsGrace(profile, companion.id, grace);
  const cost = gracePrice(grace);
  const crests = profile?.divineCrests || 0;
  const canBuy = !sold && cost != null && crests >= cost && !busy;
  const live = hovered || selected;
  const ilk = ILK_COLORS[companion.ilk] || ILK_COLORS.Neutral;

  const pick = (t) => { setGrace(t); onSelect(companion.id, t); };

  return (
    <div
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(companion.id, grace)}
      style={{
        background: `linear-gradient(135deg, ${g.color}10, transparent 60%), ${live ? OBSIDIAN_SLAB_LIT : OBSIDIAN_SLAB}`,
        border: `1px solid ${selected ? g.color : live ? g.color + "aa" : g.color + "44"}`,
        borderRadius: 12, padding: 10, display: "flex", flexDirection: "column", gap: 7,
        boxShadow: selected ? `0 0 18px ${g.color}66, ${SHADOW_RAISED}` : SHADOW_RAISED,
        cursor: "pointer", transition: "all 0.18s", position: "relative",
      }}>
      <div style={{
        position: "relative", width: "100%", aspectRatio: "1", borderRadius: 8,
        background: `radial-gradient(circle at 50% 62%, ${ilk.deep}, #07040f 72%)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        isolation: "isolate", opacity: unlocked ? 1 : 0.55,
      }}>
        <CompanionSprite companionId={companion.id} size={92} radiantTier={grace} still={!live} />
        {sold && (
          <div style={{ position: "absolute", bottom: 6, left: 6, zIndex: 3,
            background: "rgba(0,0,0,0.72)", border: `1px solid ${g.color}`, borderRadius: 8,
            padding: "1px 6px", fontSize: 7, fontWeight: 900, color: g.color, letterSpacing: 1,
            display: "flex", alignItems: "center", gap: 3 }}>
            <SourceSigil source="grace_vault" size={11} /> OWNED
          </div>
        )}
        {!unlocked && (
          <div style={{ position: "absolute", top: 6, left: 6, zIndex: 3, fontSize: 9, color: GM.vellumFaint }}>🔒</div>
        )}
      </div>

      <div style={{ fontSize: 10.5, fontWeight: 900, fontStyle: "italic", color: unlocked ? GM.vellum : GM.vellumFaint,
        textAlign: "center", lineHeight: 1.15, minHeight: 24, fontFamily: GM.font }}>
        {companion.name}
        <div style={{ fontSize: 7, fontStyle: "normal", letterSpacing: 2, color: ilk.primary, textTransform: "uppercase", marginTop: 2 }}>
          {companion.isTrophy ? "trophy" : companion.tier}
        </div>
      </div>

      <GraceGems value={grace} onPick={pick} />

      <div style={{ textAlign: "center", fontSize: 8.5, fontWeight: 900, color: g.color, letterSpacing: 1,
        fontFamily: GM.font, textShadow: `0 0 8px ${g.color}44` }}>
        {g.glyph} {g.label.toUpperCase()} · {g.crown.toUpperCase()}
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); if (canBuy) onBuy(companion, grace); }}
        title={sold ? "The Vault sells this Grace once per account." : !unlocked ? `You'll wear it once you have ${companion.name} — ${getUnlockHint(companion.id)}` : undefined}
        style={{
          width: "100%", padding: "7px", borderRadius: 8,
          cursor: canBuy ? "pointer" : sold ? "default" : "not-allowed",
          background: canBuy ? `${g.color}1e` : "transparent",
          border: `1px solid ${sold ? g.color + "55" : canBuy ? g.color : GM.pageBorder}`,
          color: sold ? GM.vellumFaint : canBuy ? g.color : GM.vellumFaint,
          fontSize: 10, fontWeight: 900, fontFamily: GM.font,
        }}>
        {sold ? "◆ CLAIMED"
          : busy ? "..."
          : cost == null ? "—"
          : <><DivineCrest size={10} /> {cost}{heldElsewhere ? " · another copy held" : ""}</>}
      </button>
    </div>
  );
}

// ── the one live picture ────────────────────────────────────────────
function FittingRoom({ companionId, grace, profile }) {
  const c = COMPANIONS[companionId];
  const g = GRACES[grace];
  if (!c || !g) return null;
  const hasBody = !!companionBodyOf(companionId);
  return (
    <div style={{
      display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap",
      padding: "12px 14px", marginBottom: 14, borderRadius: 12,
      background: `radial-gradient(ellipse at 20% 60%, ${g.color}18, transparent 60%), ${OBSIDIAN_SLAB}`,
      border: `1px solid ${g.color}55`, boxShadow: SHADOW_RAISED,
    }}>
      <div style={{ position: "relative", width: 200, height: 200, flexShrink: 0, isolation: "isolate",
        background: "radial-gradient(circle at 50% 70%, #1c1530, #07040f 72%)", borderRadius: 10 }}>
        {hasBody
          ? <CompanionBody3D companionId={companionId} size={200} grace={grace} mood="idle" />
          : <CompanionSprite companionId={companionId} size={200} radiantTier={grace} />}
      </div>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 9, letterSpacing: 3, color: g.color, fontWeight: 900, fontFamily: GM.font }}>
          THE FITTING ROOM · {g.crown.toUpperCase()}
        </div>
        <div style={{ fontSize: 18, fontWeight: 900, fontStyle: "italic", color: GM.inkGoldBright, fontFamily: GM.font, margin: "4px 0" }}>
          {c.name} — {g.label}
        </div>
        <div style={{ fontSize: 10.5, color: GM.vellumDim, fontStyle: "italic", lineHeight: 1.6, textShadow: TEXT_SET }}>
          <b style={{ color: g.color }}>{g.title}.</b> {g.blurb}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, fontSize: 9, color: GM.vellumFaint }}>
          <SourceSigil source="grace_vault" size={22} />
          A Vault Grace wears the Vault's crowned keyhole on its Mark. The same Grace won in the story is a rarer copy with its own mark.
        </div>
        {!isCompanionUnlocked(profile, companionId) && (
          <div style={{ marginTop: 6, fontSize: 9, color: GM.inkRed, fontStyle: "italic" }}>
            You can buy this now, but you'll only see and wear it once you have {c.name} — {getUnlockHint(companionId)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GraceVault({ profile, uid, onMessage }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [sel, setSel] = useState({ companionId: profile?.equippedCompanion && COMPANIONS[profile.equippedCompanion] ? profile.equippedCompanion : "spectral_tiger_lord", grace: "holo_gold" });

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.values(COMPANIONS)
      .filter((c) => {
        if (filter === "mine") return isCompanionUnlocked(profile, c.id);
        if (filter === "trophy") return !!c.isTrophy;
        if (filter !== "all") return !c.isTrophy && c.tier === filter;
        return true;
      })
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.id.includes(q))
      .sort((a, b) => {
        const ua = isCompanionUnlocked(profile, a.id), ub = isCompanionUnlocked(profile, b.id);
        if (ua !== ub) return ua ? -1 : 1;
        return (TIER_RANK[a.tier] || 0) - (TIER_RANK[b.tier] || 0) || a.name.localeCompare(b.name);
      });
  }, [profile, query, filter]);

  const handleBuy = async (companion, grace) => {
    const who = uid || profile?.uid;
    if (!who) { onMessage && onMessage("Sign in first."); return; }
    const key = `${companion.id}~${grace}`;
    setBusy(key);
    try {
      const r = await buyGrace(who, companion.id, grace, "terror");
      const g = GRACES[grace];
      if (r.success) {
        onMessage && onMessage(r.alreadyOwned
          ? `✦ You already hold ${companion.name}'s ${g.label} Grace from the Vault.`
          : isCompanionUnlocked(profile, companion.id)
            ? `✦ ${companion.name} — ${g.label} ${g.crown} secured. Wear it from the Menagerie.`
            : `✦ ${companion.name} — ${g.label} ${g.crown} secured. You'll see it once ${companion.name} is yours.`);
      } else {
        onMessage && onMessage(r.reason || "Purchase failed. Try again.");
      }
    } catch (e) {
      onMessage && onMessage("Purchase failed. Try again.");
    }
    setBusy(null);
  };

  return (
    <div>
      <FittingRoom companionId={sel.companionId} grace={sel.grace} profile={profile} />

      <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search companions…"
        style={{
          width: "100%", boxSizing: "border-box", padding: "10px 14px", marginBottom: 10,
          background: OBSIDIAN_RECESS, border: `1px solid ${GM.pageBorder}`, borderRadius: 10,
          boxShadow: SHADOW_RECESS, color: GM.vellum, fontSize: 12, fontFamily: GM.font, outline: "none",
        }} />
      <div style={{ display: "flex", gap: 6, marginBottom: 14, overflowX: "auto", paddingBottom: 4 }}>
        {TIER_FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: "7px 13px", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap",
              clipPath: facetTB(7), border: "none",
              background: active ? GOLD_METAL : OBSIDIAN_SLAB,
              color: active ? GM.page : GM.vellumDim, cursor: "pointer", fontFamily: GM.font,
            }}>{f.label}</button>
          );
        })}
      </div>
      <div style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 1, marginBottom: 10, fontFamily: GM.font }}>
        {list.length} COMPANION{list.length === 1 ? "" : "S"} · {TERROR_GRACE_ORDER.length} GRACES EACH
      </div>
      {list.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: GM.vellumFaint, fontStyle: "italic", fontSize: 12 }}>
          No companions match.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(128px, 1fr))", gap: 12 }}>
          {list.map((c) => (
            <GraceTile key={c.id} companion={c} profile={profile}
              selected={sel.companionId === c.id}
              onSelect={(companionId, grace) => setSel({ companionId, grace })}
              busy={busy && busy.startsWith(c.id + "~")}
              onBuy={handleBuy} />
          ))}
        </div>
      )}
    </div>
  );
}

// exported for the intro plaque in BoosterPack's FoilVault
export const GRACE_VAULT_BLURB = "Every Grace the Vault sells, worn by the companion at your side. Tap a companion to try it on; tap the gems to change the Grace.";
